import { ChangeDetectorRef, Component, Input, SimpleChanges } from '@angular/core';
import { ExtendedData } from '../../bb-charts/bb-chart/bb-chart.interfaces';
import { formatDate } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
    createDateRange,
    getPeriodsMap,
    getPeriods,
    getScales,
    Period,
    RangeScale
} from '../../utils/chart-utils';
import { startOfYear, subMonths, subWeeks, subYears } from 'date-fns';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    of,
    Subject,
    throwError
} from 'rxjs';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    take,
    takeUntil
} from 'rxjs/operators';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { SettingsQuery } from '../../../features/settings/state/settings.query';
import { SbEventBusService, SbEventTopic } from '@services';
import { SbChart } from '../../bb-charts/bb-chart/bb-chart.component';
import { Moment } from 'moment';
import { UiQuery } from '../../state/ui/ui.query';
import { distinctUntilArrayItemChanged, filterNilValue } from '@datorama/akita';
import { HttpClient } from '@angular/common/http';
import { NumberUtils } from '../../utils/number-utils';
import { AccountsQuery } from '../../state/accounts/accounts.query';
import {
    MARKET_VALUE_OVER_TIME_CHART_OPTIONS,
    SbMarketValueOverTimeChartService
} from '../../services/sb-market-value-over-time-chart.service';
import {
    ApiRequest,
    MarketValueData,
    RequestData
} from './sb-market-value-over-time-chart.model';
import { UserSettingsQuery } from '../../../features/user-settings/state/user-settings.query';

@Component({
    selector: 'sb-market-value-over-time-chart',
    templateUrl: './sb-market-value-over-time-chart.component.html',
    styleUrls: ['./sb-market-value-over-time-chart.component.scss']
})
export class SbMarketValueOverTimeChartComponent {
    private routerUrl: string;
    //region Input fields for API request
    accountIds: number[];
    portfolioCurrency: string;
    fxRates: { [currency: string]: number };
    fromDate: string;
    toDate: string;
    //endregion

    minDate: Date;
    minDate$: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
    maxDate: Date;
    marketValueData: MarketValueData | null = null;
    reactiveForm: UntypedFormGroup;
    unsubscribe$ = new Subject();
    loading = false;
    showDateNotice = false;
    showPeriodSelector = true;
    showScaleSelector = true;
    scale = getScales();
    periods: Array<Period> = getPeriods();

    period$ = new BehaviorSubject<Period>({
        ...getPeriodsMap().YTD
    });
    scale$: BehaviorSubject<RangeScale> = new BehaviorSubject('endOfDay');
    range$ = combineLatest([
        this.period$.pipe(filter((period) => !!period.start && !!period.end)),
        this.scale$.pipe(),
        this.minDate$.pipe(
            filterNilValue(),
            distinctUntilChanged((prev, curr) => prev === curr)
        )
    ]).pipe(
        map(([period, scale, minDate]) => {
            // if requested period is before minDate (first data point we have), set start to minDate
            let startDate = period.start;
            if (period.start < minDate) {
                startDate = minDate;
                this.showDateNotice = true;
            } else {
                this.showDateNotice = false;
            }
            return createDateRange(startDate, period.end, scale);
        }),
        distinctUntilArrayItemChanged(),
        shareReplay(1)
    );
    series$: BehaviorSubject<MarketValueData | null> =
        new BehaviorSubject<MarketValueData | null>(null);

    chartConfig$ = combineLatest([
        this.range$,
        this.series$,
        this._userSettingsQuery.selectGlobalSetting$('LOCALE')
    ]).pipe(
        map(([range, marketValueData, locale]) => {
            let columns = [];
            let lastNonNullValue = null;

            if (marketValueData) {
                const tolerance = 3600 * 5 * 1000;
                const yValues = range.map((date) => {
                    const dataPoint = marketValueData.data.find(
                        (d) => Math.abs(d[0] * 1000 - date.getTime()) <= tolerance // Check if within tolerance range
                    );

                    if (dataPoint) {
                        // Update last non-null value if data point is found
                        lastNonNullValue = dataPoint[1];
                    }
                    // Use the data point value if found, otherwise use the last non-null value
                    return lastNonNullValue;
                });

                columns = [[marketValueData.name, ...yValues]];
            }

            return {
                data: {
                    x: 'x',
                    columns: [['x', ...range], ...columns]
                },
                options: {
                    ...MARKET_VALUE_OVER_TIME_CHART_OPTIONS,
                    axis: {
                        ...MARKET_VALUE_OVER_TIME_CHART_OPTIONS.axis,
                        x: {
                            ...MARKET_VALUE_OVER_TIME_CHART_OPTIONS.axis.x,
                            tick: {
                                format: (x) => {
                                    return formatDate(x, 'd/M', locale);
                                }
                            }
                        }
                    }
                }
            };
        }),
        shareReplay(1)
    );

    //Removed temporarily: https://swimbird.slack.com/archives/C05693AKVU7/p1696325181088069
    //public currentTotalMarketValue: string;
    public absoluteChange: string;
    private _chartData$: BehaviorSubject<ExtendedData> = new BehaviorSubject({});

    constructor(
        private accountsQuery: AccountsQuery,
        private uiQuery: UiQuery,
        private settingsQuery: SettingsQuery,
        private _userSettingsQuery: UserSettingsQuery,
        private _cdr: ChangeDetectorRef,
        private _sbEventBusService: SbEventBusService,
        private _fb: UntypedFormBuilder,
        public ui: UiQuery,
        private http: HttpClient,
        private service: SbMarketValueOverTimeChartService,
        private router: Router,
        private route: ActivatedRoute
    ) {}

    get chartData(): ExtendedData {
        return this._chartData$.value;
    }

    set chartData(value: ExtendedData) {
        this._chartData$.next(<any>value);
    }

    private _chartRef: SbChart;

    get chartRef(): SbChart {
        return this._chartRef;
    }

    set chartRef(value: SbChart) {
        this._chartRef = value;
    }
    private loadData(request: ApiRequest) {
        if (request.accountIds.includes(-1)) {
            return;
        }
        this.service.postMarketValueData(request).subscribe((res) => {
            //sort dates
            res[0]?.data?.sort((a, b) => a[0] - b[0]);

            this.marketValueData = res[0]; // Update marketValueData property

            if (this.marketValueData) {
                this.series$.next(this.marketValueData); // Replace "scaleValue" with an actual value
                this.minDate$.next(new Date(request.from_date));
                this.setPeriod({
                    name: '1M',
                    start: subMonths(new Date(request.to_date), 1),
                    end: new Date(request.to_date),
                    showDateSelector: false
                });
                this.setScale('endOfDay');
                /*this.currentTotalMarketValue = NumberUtils.formatNumberToSeperated(
                    SbMarketValueOverTimeChartService.getCurrentTotalMarketValue(res)
                );*/

                const rawAbsoluteChange =
                    SbMarketValueOverTimeChartService.getAbsoluteChange(res);
                this.absoluteChange =
                    SbMarketValueOverTimeChartService.refineAbsoluteChangeLabel(
                        rawAbsoluteChange
                    );
            }
        });
    }

    isSelected(o1, o2) {
        return o1.name === o2;
    }

    setPeriod(period: Period) {
        let newPeriod: Period = { ...period, showDateSelector: false };

        if (newPeriod.name === 'Custom') {
            newPeriod.start = this.reactiveForm.value.fromDate?.toDate
                ? this.reactiveForm.value.fromDate?.toDate()
                : null;
            newPeriod.end = this.reactiveForm.value.toDate?.toDate
                ? this.reactiveForm.value.toDate?.toDate()
                : null;
            newPeriod.showDateSelector = true;
        } else if (newPeriod.name === 'All') {
            newPeriod.start = this.minDate;
            newPeriod.end = this.maxDate;
        }

        this.period$.next({ ...newPeriod });
    }

    setScale(scale: RangeScale) {
        this.scale$.next(scale);
    }

    ngOnInit() {
        this.routerUrl = this.router.url;
        this.clearDateFields();

        this.setPeriod({
            name: '1M',
            start: subMonths(new Date(), 1),
            end: new Date()
        });

        this.setScale('endOfDay');

        this.handleReloadWhenAccountsSelectionIsChanged();

        this.handleCustomDateChange();

        let initialRequest = this.generateRequest();
        this.loadData(initialRequest);

        this.setupRouterSubscription();
    }

    private setupRouterSubscription() {
        this.router.events
            .pipe(
                takeUntil(this.unsubscribe$),
                filter((event) => event instanceof NavigationEnd),
                filter(
                    () =>
                        !this.routerUrl.includes(
                            'sb-grid/assets-and-liabilities-overview'
                        )
                )
            )
            .subscribe(() => {
                let request = this.generateRequest();
                this.loadData(request);
            });
    }

    private handleCustomDateChange() {
        this.reactiveForm.valueChanges
            .pipe(
                filter((dates) => dates.fromDate !== '' && dates.toDate !== ''),
                map((dates) => ({
                    start: (dates.fromDate as Moment)?.toDate(),
                    end: (dates.toDate as Moment)?.toDate()
                })),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((period) => {
                if (period.start <= period.end) {
                    this.period$.next({
                        name: 'Custom',
                        start: period.start,
                        end: period.end,
                        showDateSelector: false
                    });
                } else {
                    this.reactiveForm.get('toDate').setErrors({ invalidDate: true });
                }
            });
    }

    private handleReloadWhenAccountsSelectionIsChanged() {
        this._sbEventBusService
            .getObservable(SbEventTopic.AccountSelected)
            .pipe(debounceTime(1000), takeUntil(this.unsubscribe$))
            .subscribe(
                (event) => {
                    let request = this.generateRequest(event.payload.value);
                    this.loadData(request);
                },
                (error) => console.error(error)
            );
    }

    private clearDateFields() {
        this.reactiveForm = this._fb.group({
            fromDate: '',
            toDate: ''
        });
    }

    generateRequest(accountIds?: number[]): RequestData {
        let fx_rates;
        this.settingsQuery.currenciesAndRates$.pipe(take(1)).subscribe((res) => {
            fx_rates = res;
        });

        if (
            (!this.router.url.includes('accountIds=') &&
                (!accountIds || !accountIds.length)) ||
            (this.router.url.includes('accountIds=') && accountIds && accountIds.length)
        ) {
            accountIds = [-1];
        } else if (
            this.router.url.includes('accountIds=') &&
            (!accountIds || !accountIds.length)
        ) {
            const urlAccountId = this.route.snapshot.queryParamMap.get('accountIds');
            accountIds = urlAccountId ? [parseInt(urlAccountId, 10)] : [-1];
        }

        const accountEntities = this.accountsQuery.getAll();
        accountIds = accountIds.concat(accountEntities.map((account) => account.value));

        let currentCurrency: string;
        const portfolio_currency = this.uiQuery.select('currentCurrency');
        portfolio_currency.pipe(take(1)).subscribe((ret) => {
            currentCurrency = ret;
        });

        return {
            accountIds: accountIds,
            from_date: formatDate(
                this.reactiveForm.value.fromDate
                    ? new Date(this.reactiveForm.value.fromDate)
                    : subYears(new Date(), 10),
                'yyyy-MM-dd',
                'en-US'
            ),
            fx_rates: fx_rates,
            portfolio_currency: currentCurrency,
            to_date: formatDate(
                this.reactiveForm.value.toDate
                    ? new Date(this.reactiveForm.value.toDate)
                    : new Date(),
                'yyyy-MM-dd',
                'en-US'
            )
        };
    }

    ngOnDestroy() {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
